/**
 * Warehouse & Inventory Management Dashboard API Client.
 * 100% Real PostgreSQL database data only — no mock data.
 */
import { apiFetch } from '../apiFetch'

export type MaterialCategory =
  | 'ALL'
  | 'go'
  | 'van'
  | 'nk'
  | 'bb'
  | 'td'
  | 'other'
  | 'WOOD'
  | 'HARDWARE'
  | 'CHEMICAL'
  | 'EDGEBANDING'
  | 'PACKAGING'
  | 'OTHER'

export const MATERIAL_CATEGORY_LABELS: Record<string, string> = {
  ALL: 'Tất cả',
  go: 'Gỗ',
  van: 'Ván',
  nk: 'Ngũ kim',
  bb: 'Bao bì',
  td: 'Trang trí',
  other: 'Chưa phân nhóm',
}

export type DatePreset =
  | 'today'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'custom'

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Hôm nay',
  last_7_days: '7 ngày qua',
  last_30_days: '30 ngày qua',
  this_month: 'Tháng này',
  last_month: 'Tháng trước',
  this_quarter: 'Quý này',
  this_year: 'Năm nay',
  custom: 'Tùy chọn',
}

export type KpiGroupKey =
  | 'all'
  | 'physical_stock'
  | 'available_stock'
  | 'active_hold'
  | 'out_of_stock'
  | 'below_min'
  | 'slow_moving'
  | 'unpriced'

export interface InventoryItemSummary {
  id: string
  material_code: string
  material_name: string
  category: MaterialCategory
  category_name: string
  uom: string
  physical_stock: number
  available_stock: number
  reserved_stock: number
  min_stock: number
  unit_price: number | null
  days_inactive: number
  last_movement_date: string
  status_code: 'normal' | 'out_of_stock' | 'below_min' | 'slow_moving' | 'unpriced'
  status_label: string
}

export interface UrgentItem {
  id: string
  material_code: string
  material_name: string
  category_name: string
  uom: string
  physical_stock: number
  available_stock: number
  reserved_stock: number
  min_stock: number
  status_label: string
  status_tone: 'danger' | 'warning'
  action_hint: string
}

export interface SlowMovingItem {
  id: string
  material_code: string
  material_name: string
  category_name: string
  uom: string
  physical_stock: number
  last_movement_date: string
  days_inactive: number
  estimated_value: number | null
}

export interface OperationalDashboardData {
  totalItems: number
  totalPhysicalStock: number
  totalAvailableStock: number
  activeHoldStock: number
  outOfStockCount: number
  belowMinCount: number
  slowMovingCount: number
  unpricedStockCount: number
  completedLsxReservationsCount?: number
  urgentItems: UrgentItem[]
  slowMovingItems: SlowMovingItem[]
  drilldownItems: InventoryItemSummary[]
}

export interface MonthlyTrendPoint {
  month: string
  inbound: number
  outbound: number
  net: number
}

export interface TopOutboundItem {
  id: string
  material_code: string
  material_name: string
  category_name: string
  uom: string
  outbound_qty: number
  issue_count: number
}

export interface TopStockItem {
  id: string
  material_code: string
  material_name: string
  category_name: string
  uom: string
  physical_stock: number
  available_stock: number
  total_value: number | null
}

export interface MaterialGroupStockStatus {
  group_code: string
  group_name?: string
  total: number
  out_of_stock: number
  below_min: number
  slow_moving: number
  normal: number
}

export interface ManagementDashboardData {
  inboundQty: number
  inboundVoucherCount: number
  outboundQty: number
  outboundVoucherCount: number
  netChangeQty: number
  openingStock: number
  closingStock: number
  totalValuationVnd: number
  unpricedQty: number
  conversionCount: number
  conversionSourceQty: number
  conversionTargetQty: number
  activeHoldCount: number
  monthlyTrend: MonthlyTrendPoint[]
  topOutboundItems: TopOutboundItem[]
  topStockItems: TopStockItem[]
  groupStockStatus: MaterialGroupStockStatus[]
}

export type DetailedReportType =
  | 'stock_available'
  | 'in_out_balance'
  | 'below_min'
  | 'slow_moving'
  | 'free_conversions'
  | 'active_reservations'

export const DETAILED_REPORT_TITLES: Record<DetailedReportType, string> = {
  stock_available: 'Báo cáo tồn kho hiện tại & khả dụng',
  in_out_balance: 'Báo cáo Nhập - Xuất - Tồn theo kỳ',
  below_min: 'Báo cáo hàng dưới định mức tối thiểu',
  slow_moving: 'Báo cáo hàng tồn lâu (> 30 ngày không phát sinh)',
  free_conversions: 'Báo cáo chuyển mã tự do sang kho chuẩn (CHT)',
  active_reservations: 'Báo cáo vật tư đang giữ hàng (Reservations)',
}

export interface ReportColumnDef {
  key: string
  label: string
  align?: 'left' | 'center' | 'right'
  isNumber?: boolean
  isCurrency?: boolean
  isDate?: boolean
  minWidth?: number
}

export interface DetailedReportsData {
  reportType: DetailedReportType
  title: string
  reportTitle?: string
  columns: ReportColumnDef[]
  rows: Record<string, unknown>[]
  summary: {
    totalRows: number
    totalPhysical?: number
    totalAvailable?: number
    totalValue?: number
  }
}

export interface DashboardFilterParams {
  category?: MaterialCategory
  preset?: DatePreset
  dateFrom?: string
  dateTo?: string
  search?: string
}

function resolveDateRange(params?: DashboardFilterParams): { startDate?: string; endDate?: string } {
  if (params?.dateFrom && params?.dateTo) {
    return { startDate: params.dateFrom, endDate: params.dateTo }
  }

  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const toYmd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

  switch (params?.preset) {
    case 'today':
      return { startDate: toYmd(now), endDate: toYmd(now) }
    case 'last_7_days': {
      const start = new Date(now.getTime() - 7 * 86400000)
      return { startDate: toYmd(start), endDate: toYmd(now) }
    }
    case 'last_30_days': {
      const start = new Date(now.getTime() - 30 * 86400000)
      return { startDate: toYmd(start), endDate: toYmd(now) }
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      return { startDate: toYmd(start), endDate: toYmd(now) }
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      return { startDate: toYmd(start), endDate: toYmd(end) }
    }
    case 'this_quarter': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3
      const start = new Date(now.getFullYear(), qMonth, 1)
      return { startDate: toYmd(start), endDate: toYmd(now) }
    }
    case 'this_year': {
      const start = new Date(now.getFullYear(), 0, 1)
      return { startDate: toYmd(start), endDate: toYmd(now) }
    }
    default:
      return {}
  }
}

export function mapCategoryToGroupCode(category?: string | null): string | undefined {
  if (!category || category === 'ALL') return undefined
  const c = category.trim()
  const lower = c.toLowerCase()
  if (lower === 'go' || lower === 'wood') return 'go'
  if (lower === 'van') return 'van'
  if (lower === 'nk' || lower === 'hardware' || lower.includes('phu_kien') || lower.includes('ngu_kim')) return 'nk'
  if (lower === 'bb' || lower === 'packaging' || lower.includes('pack') || lower.includes('dong_goi') || lower.includes('bao_bi')) return 'bb'
  if (lower === 'other' || lower === 'ungrouped' || lower === 'chua_phan_nhom') return 'other'
  if (lower === 'td' || lower.includes('chemical') || lower.includes('son') || lower.includes('nep') || lower.includes('edge')) return 'td'
  return category
}

export function mapGroupCodeToCategory(groupCode?: string | null): MaterialCategory {
  if (!groupCode) return 'other'
  const c = groupCode.trim()
  const lower = c.toLowerCase()
  if (c === 'WOOD') return 'WOOD'
  if (c === 'HARDWARE') return 'HARDWARE'
  if (c === 'PACKAGING') return 'PACKAGING'
  if (lower === 'go') return 'go'
  if (lower === 'van') return 'van'
  if (lower === 'nk') return 'nk'
  if (lower === 'bb') return 'bb'
  if (lower === 'td') return 'td'
  if (lower === 'other' || lower === 'ungrouped') return 'other'
  if (lower.includes('wood') || lower.includes('van') || lower.includes('go')) return 'go'
  if (lower.includes('hardware') || lower.includes('phu_kien') || lower.includes('ngu_kim')) return 'nk'
  if (lower.includes('pack') || lower.includes('dong_goi') || lower.includes('bao_bi')) return 'bb'
  return 'other'
}

export function getCategoryDisplayName(groupCode?: string | null): string {
  if (!groupCode) return 'Chưa phân nhóm'
  const lower = groupCode.trim().toLowerCase()
  if (MATERIAL_CATEGORY_LABELS[lower]) return MATERIAL_CATEGORY_LABELS[lower]
  if (lower === 'other' || lower === 'ungrouped' || lower === 'chua_phan_nhom' || lower === 'null') return 'Chưa phân nhóm'
  if (lower === 'wood') return 'Gỗ'
  if (lower === 'hardware') return 'Ngũ kim'
  if (lower === 'packaging') return 'Bao bì'
  return groupCode
}

/**
 * 1. GET Real Operational Dashboard Data
 */
export async function getOperationalDashboardApi(
  params?: DashboardFilterParams,
): Promise<OperationalDashboardData> {
  const query = new URLSearchParams()
  const dbGroupCode = mapCategoryToGroupCode(params?.category)
  if (dbGroupCode) {
    query.set('groupCode', dbGroupCode)
  }

  const endpointOp = `/api/reports/warehouse-dashboard/operational${query.toString() ? `?${query.toString()}` : ''}`
  const endpointDetails = `/api/reports/warehouse-dashboard/details?reportType=stock_overview${query.toString() ? `&${query.toString()}` : ''}`

  try {
    const [resOp, resDetails] = await Promise.all([
      apiFetch(endpointOp),
      apiFetch(endpointDetails),
    ])

    if (!resOp.ok) {
      throw new Error(`Operational report API error: ${resOp.status}`)
    }

    const jsonOp = await resOp.json()
    const dataOp = jsonOp?.data ?? jsonOp
    const kpis = dataOp?.kpis || {}

    const urgentItems: UrgentItem[] = (dataOp?.urgent_items || []).map((u: any) => ({
      id: u.material_id || u.id,
      material_code: u.material_code || u.code || '',
      material_name: u.material_name || u.name || '',
      category_name: getCategoryDisplayName(u.group_code),
      uom: u.uom || 'cái',
      physical_stock: Number(u.on_hand_qty) || 0,
      available_stock: Number(u.available_qty) || 0,
      reserved_stock: Number(u.reserved_qty) || 0,
      min_stock: Number(u.threshold ?? u.min_qty) || 0,
      status_label: u.urgent_reasons?.includes('OUT_OF_STOCK') ? 'Hết hàng khả dụng' : 'Dưới định mức',
      status_tone: u.urgent_reasons?.includes('OUT_OF_STOCK') ? 'danger' : 'warning',
      action_hint:
        u.active_reservations_count > 0
          ? `Đang có ${u.active_reservations_count} lệnh giữ hàng cần cấp phát`
          : 'Cần lập đơn mua hàng bổ sung',
    }))

    const slowMovingItems: SlowMovingItem[] = (dataOp?.slow_moving_items || []).map((s: any) => ({
      id: s.material_id || s.id,
      material_code: s.material_code || s.code || '',
      material_name: s.material_name || s.name || '',
      category_name: getCategoryDisplayName(s.group_code),
      uom: s.uom || 'cái',
      physical_stock: Number(s.on_hand_qty) || 0,
      last_movement_date: s.last_movement_at ? String(s.last_movement_at).slice(0, 10) : 'Chưa phát sinh',
      days_inactive: s.days_inactive != null ? Math.round(Number(s.days_inactive)) : 30,
      estimated_value: null,
    }))

    let drilldownItems: InventoryItemSummary[] = []
    if (resDetails.ok) {
      const jsonDetails = await resDetails.json()
      const dataDetails = jsonDetails?.data ?? jsonDetails
      const items: any[] = Array.isArray(dataDetails) ? dataDetails : (dataDetails?.items ?? [])

      drilldownItems = items.map((m: any) => ({
        id: m.id || m.material_id,
        material_code: m.code || m.material_code,
        material_name: m.name || m.material_name,
        category: mapGroupCodeToCategory(m.group_code),
        category_name: getCategoryDisplayName(m.group_code),
        uom: m.uom || 'cái',
        physical_stock: Number(m.on_hand_qty) || 0,
        available_stock: Number(m.available_qty) || 0,
        reserved_stock: Number(m.reserved_qty) || 0,
        min_stock: Number(m.min_qty) || 0,
        unit_price: m.unit_price != null ? Number(m.unit_price) : null,
        days_inactive: m.days_inactive != null ? Math.round(Number(m.days_inactive)) : 0,
        last_movement_date: m.last_movement_at ? String(m.last_movement_at).slice(0, 10) : '',
        status_code: m.out_of_stock ? 'out_of_stock' : m.below_threshold ? 'below_min' : 'normal',
        status_label: m.out_of_stock ? 'Hết hàng' : m.below_threshold ? 'Dưới định mức' : 'Bình thường',
      }))
    }

    return {
      totalItems: Number(kpis.total_materials) || 0,
      totalPhysicalStock: Number(kpis.total_on_hand_qty) || 0,
      totalAvailableStock: Number(kpis.total_available_qty) || 0,
      activeHoldStock: Number(kpis.active_holds_count ?? kpis.total_reserved_qty) || 0,
      outOfStockCount: Number(kpis.out_of_stock_count) || 0,
      belowMinCount: Number(kpis.below_threshold_count) || 0,
      slowMovingCount: Number(kpis.slow_moving_count) || 0,
      unpricedStockCount: Number(kpis.unpriced_count) || 0,
      completedLsxReservationsCount: Number(kpis.completed_lsx_reservations_count) || 0,
      urgentItems,
      slowMovingItems,
      drilldownItems,
    }
  } catch (err) {
    console.error('[getOperationalDashboardApi] Error:', err)
    return {
      totalItems: 0,
      totalPhysicalStock: 0,
      totalAvailableStock: 0,
      activeHoldStock: 0,
      outOfStockCount: 0,
      belowMinCount: 0,
      slowMovingCount: 0,
      unpricedStockCount: 0,
      completedLsxReservationsCount: 0,
      urgentItems: [],
      slowMovingItems: [],
      drilldownItems: [],
    }
  }
}

/**
 * 2. GET Real Management Dashboard Data
 */
export async function getManagementDashboardApi(
  params?: DashboardFilterParams,
): Promise<ManagementDashboardData> {
  const query = new URLSearchParams()
  const dbGroupCode = mapCategoryToGroupCode(params?.category)
  if (dbGroupCode) {
    query.set('groupCode', dbGroupCode)
  }

  const { startDate, endDate } = resolveDateRange(params)
  if (startDate) query.set('startDate', startDate)
  if (endDate) query.set('endDate', endDate)

  const endpoint = `/api/reports/warehouse-dashboard/management${query.toString() ? `?${query.toString()}` : ''}`

  try {
    const res = await apiFetch(endpoint)
    if (!res.ok) {
      throw new Error(`Management report API error: ${res.status}`)
    }

    const json = await res.json()
    const data = json?.data ?? json
    const summary = data?.summary || {}

    const monthlyTrend: MonthlyTrendPoint[] = (data?.monthly_trends || []).map((t: any) => ({
      month: (t.month as string) || '',
      inbound: Number(t.inbound_qty) || 0,
      outbound: Number(t.outbound_qty) || 0,
      net: Number(t.net_qty) || 0,
    }))

    const topOutboundItems: TopOutboundItem[] = (data?.top_issued_materials || []).map((t: any, idx: number) => ({
      id: (t.material_id as string) || `top-${idx}`,
      material_code: (t.material_code as string) || '',
      material_name: (t.material_name as string) || '',
      category_name: getCategoryDisplayName(t.group_code),
      uom: (t.uom as string) || 'cái',
      outbound_qty: Number(t.total_issued_qty) || 0,
      issue_count: 1,
    }))

    const topStockItems: TopStockItem[] = (data?.top_on_hand_materials || []).map((s: any, idx: number) => ({
      id: (s.material_id as string) || `stk-${idx}`,
      material_code: (s.material_code as string) || '',
      material_name: (s.material_name as string) || '',
      category_name: getCategoryDisplayName(s.group_code),
      uom: (s.uom as string) || 'cái',
      physical_stock: Number(s.on_hand_qty) || 0,
      available_stock: Number(s.on_hand_qty) || 0,
      total_value: s.total_value != null ? Number(s.total_value) : null,
    }))

    const groupStockStatus: MaterialGroupStockStatus[] = (data?.group_stock_status || []).map((g: any) => ({
      group_code: String(g.group_code || ''),
      group_name: MATERIAL_CATEGORY_LABELS[g.group_code] || getCategoryDisplayName(g.group_code),
      total: Number(g.total) || 0,
      out_of_stock: Number(g.out_of_stock) || 0,
      below_min: Number(g.below_min) || 0,
      slow_moving: Number(g.slow_moving) || 0,
      normal: Number(g.normal) || 0,
    }))

    return {
      inboundQty: Number(summary.inbound_qty) || 0,
      inboundVoucherCount: Number(summary.inbound_count) || 0,
      outboundQty: Number(summary.outbound_qty) || 0,
      outboundVoucherCount: Number(summary.outbound_count) || 0,
      netChangeQty: Number(summary.net_change_qty) || 0,
      openingStock: Number(summary.beginning_stock_qty) || 0,
      closingStock: Number(summary.ending_stock_qty) || 0,
      totalValuationVnd: Number(summary.estimated_valuation_amount) || 0,
      unpricedQty: Number(summary.unpriced_materials_count) || 0,
      conversionCount: Number(summary.free_conversions_count) || 0,
      conversionSourceQty: Number(summary.free_conversions_qty_free) || 0,
      conversionTargetQty: Number(summary.free_conversions_qty_base) || 0,
      activeHoldCount: Number(summary.reservations_created_count) || 0,
      monthlyTrend,
      topOutboundItems,
      topStockItems,
      groupStockStatus,
    }
  } catch (err) {
    console.error('[getManagementDashboardApi] Error:', err)
    return {
      inboundQty: 0,
      inboundVoucherCount: 0,
      outboundQty: 0,
      outboundVoucherCount: 0,
      netChangeQty: 0,
      openingStock: 0,
      closingStock: 0,
      totalValuationVnd: 0,
      unpricedQty: 0,
      conversionCount: 0,
      conversionSourceQty: 0,
      conversionTargetQty: 0,
      activeHoldCount: 0,
      monthlyTrend: [],
      topOutboundItems: [],
      topStockItems: [],
      groupStockStatus: [],
    }
  }
}

/**
 * 3. GET Real Detailed Reports Data
 */
export async function getDetailedReportsApi(
  params: DashboardFilterParams & { reportType: DetailedReportType },
): Promise<DetailedReportsData> {
  const backendReportTypeMap: Record<DetailedReportType, string> = {
    stock_available: 'stock_overview',
    in_out_balance: 'in_out_balance',
    below_min: 'below_threshold',
    slow_moving: 'slow_moving',
    free_conversions: 'free_conversions',
    active_reservations: 'reservations',
  }

  const backendType = backendReportTypeMap[params.reportType] || 'stock_overview'
  const query = new URLSearchParams()
  query.set('reportType', backendType)

  const dbGroupCode = mapCategoryToGroupCode(params?.category)
  if (dbGroupCode) {
    query.set('groupCode', dbGroupCode)
  }

  const { startDate, endDate } = resolveDateRange(params)
  if (startDate) query.set('startDate', startDate)
  if (endDate) query.set('endDate', endDate)
  if (params?.search) query.set('search', params.search)

  const endpoint = `/api/reports/warehouse-dashboard/details?${query.toString()}`

  try {
    const res = await apiFetch(endpoint)
    if (!res.ok) {
      throw new Error(`Detailed report API error: ${res.status}`)
    }

    const json = await res.json()
    const data = json?.data ?? json
    const rawItems: any[] = Array.isArray(data) ? data : (data?.items ?? [])

    let columns: ReportColumnDef[] = []
    let rows: Record<string, unknown>[] = []

    if (params.reportType === 'stock_available' || params.reportType === 'below_min' || params.reportType === 'slow_moving') {
      columns = [
        { key: 'material_code', label: 'Mã vật tư' },
        { key: 'material_name', label: 'Tên vật tư' },
        { key: 'category_name', label: 'Nhóm' },
        { key: 'uom', label: 'ĐVT', align: 'center' },
        { key: 'physical_stock', label: 'Tồn thực tế', align: 'right', isNumber: true },
        { key: 'available_stock', label: 'Khả dụng', align: 'right', isNumber: true },
        { key: 'reserved_stock', label: 'Đang giữ', align: 'right', isNumber: true },
        { key: 'min_stock', label: 'Định mức min', align: 'right', isNumber: true },
        { key: 'unit_price', label: 'Đơn giá gần nhất', align: 'right', isCurrency: true },
        { key: 'stock_value', label: 'Giá trị tồn', align: 'right', isCurrency: true },
        { key: 'status_label', label: 'Trạng thái' },
      ]

      rows = rawItems.map((m: any) => ({
        material_code: m.code || m.material_code,
        material_name: m.name || m.material_name,
        category_name: getCategoryDisplayName(m.group_code),
        uom: m.uom || 'cái',
        physical_stock: Number(m.on_hand_qty) || 0,
        available_stock: Number(m.available_qty) || 0,
        reserved_stock: Number(m.reserved_qty) || 0,
        min_stock: Number(m.min_qty) || 0,
        unit_price: m.unit_price != null ? Number(m.unit_price) : null,
        stock_value: Number(m.stock_value) || 0,
        status_label: m.out_of_stock ? 'Hết hàng' : m.below_threshold ? 'Dưới định mức' : 'Bình thường',
      }))
    } else if (params.reportType === 'in_out_balance') {
      columns = [
        { key: 'material_code', label: 'Mã vật tư' },
        { key: 'material_name', label: 'Tên vật tư' },
        { key: 'category_name', label: 'Nhóm' },
        { key: 'uom', label: 'ĐVT', align: 'center' },
        { key: 'beginning_qty', label: 'Tồn đầu kỳ', align: 'right', isNumber: true },
        { key: 'inbound_qty', label: 'Nhập trong kỳ', align: 'right', isNumber: true },
        { key: 'outbound_qty', label: 'Xuất trong kỳ', align: 'right', isNumber: true },
        { key: 'ending_qty', label: 'Tồn cuối kỳ', align: 'right', isNumber: true },
        { key: 'unit_price', label: 'Đơn giá', align: 'right', isCurrency: true },
        { key: 'ending_value', label: 'Giá trị cuối kỳ', align: 'right', isCurrency: true },
      ]

      rows = rawItems.map((r: any) => ({
        material_code: r.code || r.material_code,
        material_name: r.name || r.material_name,
        category_name: getCategoryDisplayName(r.group_code),
        uom: r.uom || 'cái',
        beginning_qty: Number(r.beginning_qty) || 0,
        inbound_qty: Number(r.inbound_qty) || 0,
        outbound_qty: Number(r.outbound_qty) || 0,
        ending_qty: Number(r.ending_qty) || 0,
        unit_price: r.unit_price != null ? Number(r.unit_price) : null,
        ending_value: Number(r.ending_value) || 0,
      }))
    } else if (params.reportType === 'free_conversions') {
      columns = [
        { key: 'doc_code', label: 'Mã chứng từ' },
        { key: 'created_at', label: 'Ngày chuyển', isDate: true },
        { key: 'free_item_name', label: 'Hàng tự do (Nguồn)' },
        { key: 'qty_free', label: 'SL tự do', align: 'right', isNumber: true },
        { key: 'free_uom', label: 'ĐVT nguồn', align: 'center' },
        { key: 'material_code', label: 'Mã chuẩn' },
        { key: 'material_name', label: 'Vật tư nhận (Đích)' },
        { key: 'qty_base', label: 'SL nhận', align: 'right', isNumber: true },
        { key: 'base_uom', label: 'ĐVT đích', align: 'center' },
        { key: 'factor', label: 'Hệ số', align: 'right' },
        { key: 'created_by', label: 'Người chuyển' },
        { key: 'reason', label: 'Lý do' },
        { key: 'status', label: 'Trạng thái' },
      ]

      rows = rawItems.map((f: any) => ({
        doc_code: f.code || f.doc_code || '',
        created_at: f.created_at ? String(f.created_at).slice(0, 10) : '',
        free_item_name: f.free_item_name || 'Hàng tự do',
        qty_free: Number(f.qty_free) || 0,
        free_uom: f.free_uom || '',
        material_code: f.material_code || '',
        material_name: f.material_name || '',
        qty_base: Number(f.qty_base) || 0,
        base_uom: f.base_uom || '',
        factor: f.factor != null ? Number(f.factor) : 1,
        created_by: f.created_by || '',
        reason: f.reason || '',
        status: f.status || 'ACTIVE',
      }))
    } else if (params.reportType === 'active_reservations') {
      columns = [
        { key: 'material_code', label: 'Mã vật tư' },
        { key: 'material_name', label: 'Tên vật tư' },
        { key: 'uom', label: 'ĐVT', align: 'center' },
        { key: 'qty_remaining', label: 'SL đang giữ', align: 'right', isNumber: true },
        { key: 'demand_type', label: 'Loại nhu cầu' },
        { key: 'order_title', label: 'Đơn hàng / LSX' },
        { key: 'customer_name', label: 'Khách hàng' },
        { key: 'created_by', label: 'Người giữ' },
        { key: 'created_at', label: 'Ngày giữ', isDate: true },
        { key: 'status', label: 'Trạng thái' },
        { key: 'note', label: 'Ghi chú' },
      ]

      rows = rawItems.map((r: any) => ({
        material_code: r.material_code || '',
        material_name: r.material_name || '',
        uom: r.uom || 'cái',
        qty_remaining: Number(r.qty_remaining ?? r.qty_reserved) || 0,
        demand_type: r.demand_type || 'MANUAL_HOLD',
        order_title: r.order_title || '—',
        customer_name: r.customer_name || '—',
        created_by: r.created_by || '',
        created_at: r.created_at ? String(r.created_at).slice(0, 10) : '',
        status: r.status || 'ACTIVE',
        note: r.note || '',
      }))
    }

    return {
      reportType: params.reportType,
      title: DETAILED_REPORT_TITLES[params.reportType],
      reportTitle: DETAILED_REPORT_TITLES[params.reportType],
      columns,
      rows,
      summary: {
        totalRows: rows.length,
      },
    }
  } catch (err) {
    console.error('[getDetailedReportsApi] Error:', err)
    return {
      reportType: params.reportType,
      title: DETAILED_REPORT_TITLES[params.reportType],
      reportTitle: DETAILED_REPORT_TITLES[params.reportType],
      columns: [],
      rows: [],
      summary: { totalRows: 0 },
    }
  }
}
